import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { closeRequest, markQuoteSent, patchRequestStatus } from '../../../shared/api/requests.api'
import { getQuotations } from '../../../shared/api/quotations.api'
import { createFollowUp, getFollowUps } from '../../../shared/api/followUps.api'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { toast } from '../../../shared/ui/toastStore'
import { useAuthStore } from '../../auth/authStore'
import { RejectionReasonModal } from './RejectionReasonModal'
import { WonQuotationModal } from './WonQuotationModal'
import { CreateInvoiceModal } from '../../orders/components/CreateInvoiceModal'
import { FollowUpModal } from './FollowUpModal'
import type { RequestItem } from '../../../shared/api/types'

// Статусы, в которых КП уже отправлено клиенту либо заявка закрыта —
// кнопку «КП отправлено» в них показывать не нужно.
const SENT_OR_CLOSED = ['QUOTE_SENT', 'CLOSED_WON', 'CLOSED_LOST']

/**
 * Блок «Действия» под таблицей позиций. Кнопки зависят от статуса заявки:
 *   QUOTATION_CREATED → баннер-напоминалка + «КП отправлено» + откат к пересборке
 *   QUOTE_SENT        → «Сделка» / «Отказ» + откат к «Готова к КП»
 *   CLOSED_WON/LOST   → откат к «КП отправлено» (снова появятся Сделка/Отказ)
 *
 * Остальные статусы автомат обслуживает сам (IN_PROGRESS, QUOTATION_READY и др.) —
 * менеджер двигает воркфлоу через «Выставить КП» в режиме КП.
 */
export function ActionsPanel({
  requestId,
  status,
  sellerEntityId,
  items,
}: {
  requestId: string
  status: string
  /** Юрлицо продажи заявки — по умолчанию в окне счёта после «Сделки». */
  sellerEntityId?: string | null
  items: RequestItem[]
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const role = user?.role
  const [error, setError] = useState<unknown>(null)
  // После «Сделка → выставить счёт» позиции уходят в «Счёт» — сразу открываем
  // модал оформления заказа (создание заказа + выгрузка шаблона счёта в УНФ).
  const [showCreateInvoice, setShowCreateInvoice] = useState(false)

  const inv = () => {
    void queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
    // КП-список тоже инвалидируем — orderedCount меняется после оформления.
    void queryClient.invalidateQueries({ queryKey: ['quotations', requestId] })
    // «Контроль КП»: закрытие заявки авто-отменяет напоминание на бэке —
    // без инвалидации блок показывал бы его активным до перезагрузки.
    void queryClient.invalidateQueries({ queryKey: ['follow-ups', requestId] })
    void queryClient.invalidateQueries({ queryKey: ['follow-up-alerts'] })
  }

  // «Контроль КП»: после «КП отправлено» предлагаем назначить следующий контакт
  // с клиентом (пропускаемая модалка). Если активное напоминание уже есть —
  // не спрашиваем повторно.
  const [showFollowUpModal, setShowFollowUpModal] = useState(false)
  // Заметки «Контроля КП» сервер отдаёт только продажной линии и ГД (№28,
  // 14.09.2026). РОЗ видит панель «Действия», но заметки не запрашивает.
  const canReadFollowUps =
    role === 'ADMIN' ||
    role === 'GENERAL_DIRECTOR' ||
    role === 'MANAGER' ||
    role === 'ROP' ||
    role === 'ASSISTANT'
  const { data: followUps = [] } = useQuery({
    queryKey: ['follow-ups', requestId],
    queryFn: () => getFollowUps(requestId),
    enabled: canReadFollowUps,
  })
  const hasOpenFollowUp = followUps.some((f) => f.status === 'OPEN')

  const sentMut = useMutation({
    mutationFn: () => markQuoteSent(requestId),
    onSuccess: () => {
      inv()
      if (!hasOpenFollowUp) setShowFollowUpModal(true)
    },
    onError: setError,
  })
  const closeMut = useMutation({
    mutationFn: (p: {
      outcome: 'WON' | 'LOST'
      reason?: string
      reasonCode?: string
      quotationId?: string
    }) =>
      closeRequest(
        requestId,
        p.outcome,
        p.reason ?? null,
        p.quotationId ?? null,
        p.reasonCode ?? null,
      ),
    onSuccess: inv,
    onError: setError,
  })

  // Модалка «Причина отказа» — открывается при клике на «Отказ ✗»,
  // пока не заполнен reason — mutation не вызывается.
  const [showRejectModal, setShowRejectModal] = useState(false)
  // Модалка выбора КП — открывается при клике на «Сделка ✓».
  const [showWonModal, setShowWonModal] = useState(false)
  const rollbackMut = useMutation({
    mutationFn: (target: 'QUOTATION_READY' | 'QUOTE_SENT') => patchRequestStatus(requestId, target),
    onSuccess: inv,
    onError: setError,
  })

  const canAct = role === 'MANAGER' || role === 'ADMIN' || role === 'ROP' || role === 'ASSISTANT'

  // Наличие хотя бы одного КП — тот же запрос, что у KPAccordion (кеш общий).
  const { data: quotations = [], refetch: refetchQuotations } = useQuery({
    queryKey: ['quotations', requestId],
    queryFn: () => getQuotations(requestId),
  })
  const hasQuotations = quotations.length > 0

  // Кнопка «КП отправлено» должна появляться сразу после выставления хотя бы
  // одного КП — независимо от того, перешла ли заявка в QUOTATION_CREATED.
  // Раньше она была завязана строго на статус QUOTATION_CREATED, и при
  // выставлении КП из IN_PROGRESS заявка зависала без кнопки.
  const canMarkSent = canAct && hasQuotations && !SENT_OR_CLOSED.includes(status)

  // Сетка-счётчик «Всего/Готовы/В работе/Нужна инфо» убрана 28.04 по
  // запросу Сергея: в реальной работе она была неинформативна (часто
  // показывала 0/0/0/0 для готовых заявок). Ту же информацию даёт
  // строка «Действия» в шапке заявки и фильтр по колонке статус.
  // useQuery getQuotationReadiness оставляем — при необходимости вернём.

  return (
    <div className="space-y-3">
      {error != null && <ErrorMessage error={error} />}

      {canMarkSent && (
        <>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            📝 КП создано. Не забудьте нажать «КП отправлено» после отправки клиенту.
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => sentMut.mutate()}
              disabled={sentMut.isPending}
              className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {sentMut.isPending ? '...' : '✉ КП отправлено'}
            </button>
            {status === 'QUOTATION_CREATED' && (
              <button
                onClick={() => rollbackMut.mutate('QUOTATION_READY')}
                disabled={rollbackMut.isPending}
                className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Вернуться к пересборке КП
              </button>
            )}
          </div>
        </>
      )}

      {status === 'QUOTE_SENT' && canAct && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              // Подтягиваем свежий список КП — если КП удалили/обновили,
              // модалка покажет актуальное.
              void refetchQuotations()
              setShowWonModal(true)
            }}
            disabled={closeMut.isPending}
            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            Сделка ✓
          </button>
          <button
            onClick={() => setShowRejectModal(true)}
            disabled={closeMut.isPending}
            className="px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
          >
            Отказ ✗
          </button>
          <button
            onClick={() => rollbackMut.mutate('QUOTATION_READY')}
            disabled={rollbackMut.isPending}
            className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Вернуться к КП
          </button>
        </div>
      )}

      {(status === 'CLOSED_WON' || status === 'CLOSED_LOST') && canAct && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => rollbackMut.mutate('QUOTE_SENT')}
            disabled={rollbackMut.isPending}
            className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            ↩ Вернуться к «КП отправлено»
          </button>
        </div>
      )}

      {showRejectModal && (
        <RejectionReasonModal
          onConfirm={async (reason, reasonCode) => {
            await closeMut.mutateAsync({ outcome: 'LOST', reason, reasonCode })
            setShowRejectModal(false)
          }}
          onCancel={() => setShowRejectModal(false)}
        />
      )}

      {showWonModal && (
        <WonQuotationModal
          requestId={requestId}
          quotations={quotations}
          items={items}
          onConfirm={async (quotationId) => {
            await closeMut.mutateAsync({
              outcome: 'WON',
              quotationId: quotationId ?? undefined,
            })
            setShowWonModal(false)
            // Позиции выбранного КП ушли в «Счёт» — открываем оформление заказа.
            setShowCreateInvoice(true)
          }}
          onCancel={() => setShowWonModal(false)}
        />
      )}

      {showCreateInvoice && (
        <CreateInvoiceModal
          requestId={requestId}
          defaultEntityId={sellerEntityId}
          onClose={() => setShowCreateInvoice(false)}
          onCreated={(orderId) => {
            setShowCreateInvoice(false)
            navigate(`/orders/${orderId}`)
          }}
        />
      )}

      {showFollowUpModal && (
        <FollowUpModal
          title="Когда связаться с клиентом?"
          cancelLabel="Пропустить"
          onConfirm={async (dueAt, note) => {
            await createFollowUp(requestId, { dueAt, note: note || null })
            void queryClient.invalidateQueries({ queryKey: ['follow-ups', requestId] })
            void queryClient.invalidateQueries({ queryKey: ['follow-up-alerts'] })
            void queryClient.invalidateQueries({ queryKey: ['requests'] })
            toast.success('Напоминание о контакте создано')
            setShowFollowUpModal(false)
          }}
          onCancel={() => setShowFollowUpModal(false)}
        />
      )}
    </div>
  )
}
